/* MAC Industries – laser-cut homepage hero (sections/mac-laser-hero.liquid) + laser-dot pointer.
   Ported from the standalone preview. Vanilla JS + one <canvas>. No libraries.
   Loaded only on the homepage (request.page_type == 'index'), from the section.
   Intro clip plays once -> crossfades into a full-width steel sheet -> scroll drives a laser that cuts a
   16D table top: all the holes, then the MAC logo in the clear centre panel, then the outer profile. */
(function () {
  'use strict';
  var D = document, html = D.documentElement, W0 = window, NS = 'http://www.w3.org/2000/svg';
  var root = D.querySelector('[data-mlh]');
  if (!root || root.getAttribute('data-mlh-init')) return;
  root.setAttribute('data-mlh-init', '1');
  var cfg = root.dataset;
  var RM = matchMedia('(prefers-reduced-motion: reduce)').matches || /[?&]mlh_rm=1/.test(location.search) || cfg.static === 'true';
  var FINE = matchMedia('(hover: hover) and (pointer: fine)').matches;
  // theme fonts (no font files bundled): the canvas uses the theme's heading font
  var HFONT = (getComputedStyle(root).getPropertyValue('--font-stack-header') || '').trim() || '"Arial Narrow", sans-serif';
  var mqDesk = matchMedia('(min-width: 992px)');
  var TAU = Math.PI * 2, PI = Math.PI;
  var q = function (s, c) { return (c || D).querySelector(s); };
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function seg(p, a, b) { return b > a ? clamp((p - a) / (b - a), 0, 1) : (p >= b ? 1 : 0); }
  function easeIO(t) { t = clamp(t, 0, 1); return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function easeOut(t) { t = clamp(t, 0, 1); return 1 - Math.pow(1 - t, 3); }
  function rng(seed) { return function () { seed |= 0; seed = seed + 0x6D2B79F5 | 0; var t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  if (RM) root.classList.add('mlh--rm');

  /* =====================================================================
     1. PART DEFINITION (all in millimetres)
     ---------------------------------------------------------------------
     part = { name, w, h,
              holes:[{x,y,r}]            in cut order,
              outline: contour           outer profile, cut LAST,
              logo: { groups, seq, L }   interior cut-outs (the MAC logo), cut after the holes,
              logoBox: {x,y,w,h}         where the logo SVG is placed,
              clear: {x,y,w,h}           the hole-free panel (no grid etched there),
              grid: {x0,y0,pitch,cols,rows} | null }

     buildGridPart() generates a 16D top from parameters, leaving a clear panel in the middle.

     >>> DXF HOOK: export Jack's real DXF to SVG in mm and set PART_SVG_URL (or open ?part=assets/part/16d-top.svg).
         partFromSVG() reads: <circle> = 16D holes, the largest closed shape = outer profile,
         any other closed shapes = interior cut-outs (e.g. the logo, cut after the holes).
         If the drawing has no cut-outs but has <rect id="logo-box">, LOGO_SVG_URL is placed in that box.
     >>> LOGO: assets/part/mac-logo.svg (traced from the live-site logo). Swap it for an official vector at any time.
     ===================================================================== */
  var PART_SVG_URL = cfg.part || null;          // optional section setting: SVG export of the real DXF (mm)
  var LOGO_SVG_URL = cfg.logo || null;          // theme asset mac-laser-logo.svg
  // PERF (Sep 2026): the logo outline is PRE-TRACED into mac-laser-logo-points.json (data-logo-points) so the
  // browser no longer runs ~7,000 getPointAtLength() calls (~10 s main-thread freeze on desktop, far more on phones).
  // If the section doesn't pass data-logo-points, the URL is derived from data-logo (same assets folder).
  // Regenerate the JSON (gen_points.py) whenever mac-laser-logo.svg changes.
  var LOGO_POINTS_URL = cfg.logoPoints || (LOGO_SVG_URL ? LOGO_SVG_URL.replace(/mac-laser-logo\.svg(\?.*)?$/, 'mac-laser-logo-points.json') : null);
  if (LOGO_POINTS_URL === LOGO_SVG_URL) LOGO_POINTS_URL = null;
  // desktop: full 1500 x 1000 top, 29 x 19 grid with a 13 x 5 clear panel -> 486 holes + logo
  var TOP_16D = { name: '16D table top', w: 1500, h: 1000, pitch: 50, hole: 16, margin: 50, corner: 12, clear: { cols: 13, rows: 5 }, logoInset: 20 };
  // mobile: 12 x 12 section with an 8 x 4 clear panel -> 112 holes + logo
  var SECTION_16D = { name: '16D table top', w: 650, h: 650, pitch: 50, hole: 16, margin: 50, corner: 10, clear: { cols: 8, rows: 4 }, logoInset: 14 };

  function buildGridPart(o) {
    var cols = Math.floor((o.w - 2 * o.margin) / o.pitch) + 1, rows = Math.floor((o.h - 2 * o.margin) / o.pitch) + 1;
    var x0 = (o.w - (cols - 1) * o.pitch) / 2, y0 = (o.h - (rows - 1) * o.pitch) / 2, holes = [], cl = null;
    if (o.clear) {
      var c0 = Math.round((cols - o.clear.cols) / 2), r0 = Math.round((rows - o.clear.rows) / 2);
      cl = { c0: c0, r0: r0, c1: c0 + o.clear.cols - 1, r1: r0 + o.clear.rows - 1 };
    }
    for (var r = 0; r < rows; r++) for (var c = 0; c < cols; c++) {
      var cc = r % 2 ? c : cols - 1 - c; // zig-zag, starting at the right-hand end (the visible side on desktop)
      if (cl && cc >= cl.c0 && cc <= cl.c1 && r >= cl.r0 && r <= cl.r1) continue;
      holes.push({ x: x0 + cc * o.pitch, y: y0 + r * o.pitch, r: o.hole / 2 });
    }
    var pt = { name: o.name, w: o.w, h: o.h, holes: holes, outline: makeContour(roundedRectPoly(o.w, o.h, o.corner)),
      grid: { x0: x0, y0: y0, pitch: o.pitch, cols: cols, rows: rows }, logo: null };
    if (cl) {
      pt.clear = { x: x0 + (cl.c0 - .5) * o.pitch, y: y0 + (cl.r0 - .5) * o.pitch, w: o.clear.cols * o.pitch, h: o.clear.rows * o.pitch };
      pt.logoBox = { x: pt.clear.x + o.logoInset, y: pt.clear.y + o.logoInset, w: pt.clear.w - 2 * o.logoInset, h: pt.clear.h - 2 * o.logoInset };
    }
    return finishPart(pt);
  }
  function roundedRectPoly(w, h, r) {
    var p = [], n = 10;
    function arc(cx, cy, a0) { for (var i = 1; i <= n; i++) { var a = a0 + i / n * PI / 2; p.push(cx + r * Math.cos(a), cy + r * Math.sin(a)); } }
    p.push(r, 0, w - r, 0); arc(w - r, r, -PI / 2);
    p.push(w, h - r); arc(w - r, h - r, 0);
    p.push(r, h); arc(r, h - r, PI / 2);
    p.push(0, r); arc(r, r, PI);
    return p;
  }
  /* closed polyline -> { pts, cum, n, len, path, area, bbox, start, pierce } */
  function makeContour(a) {
    a = a.slice(); var n = a.length / 2;
    if (a[0] !== a[a.length - 2] || a[1] !== a[a.length - 1]) { a.push(a[0], a[1]); n++; }
    var pts = new Float32Array(a), cum = new Float32Array(n), area = 0, path = new Path2D(), bx0 = 1e9, by0 = 1e9, bx1 = -1e9, by1 = -1e9;
    path.moveTo(pts[0], pts[1]);
    for (var i = 0; i < n; i++) {
      var x = pts[i * 2], y = pts[i * 2 + 1];
      if (x < bx0) bx0 = x; if (x > bx1) bx1 = x; if (y < by0) by0 = y; if (y > by1) by1 = y;
      if (!i) continue;
      var dx = x - pts[i * 2 - 2], dy = y - pts[i * 2 - 1];
      cum[i] = cum[i - 1] + Math.sqrt(dx * dx + dy * dy);
      area += pts[i * 2 - 2] * y - x * pts[i * 2 - 1];
      path.lineTo(x, y);
    }
    path.closePath();
    var k = 1; while (k < n - 1 && cum[k] < 1e-6) k++;
    var fx = pts[k * 2] - pts[0], fy = pts[k * 2 + 1] - pts[1], fl = Math.sqrt(fx * fx + fy * fy) || 1; fx /= fl; fy /= fl;
    var nx = area > 0 ? fy : -fy, ny = area > 0 ? -fx : fx; // outward normal of the first edge
    return { pts: pts, cum: cum, n: n, len: cum[n - 1], path: path, area: Math.abs(area / 2), bbox: { x: bx0, y: by0, w: bx1 - bx0, h: by1 - by0 },
      start: { x: pts[0], y: pts[1] }, pierce: { x: pts[0] + nx * 14, y: pts[1] + ny * 14 } };
  }
  function finishPart(pt) {
    var minY = 1e9, maxY = -1e9; pt.holes.forEach(function (h) { minY = Math.min(minY, h.y); maxY = Math.max(maxY, h.y); });
    pt.minY = minY; pt.maxY = maxY;
    return pt;
  }
  function contourAt(o, L, out) {
    var lo = 0, hi = o.n - 1; L = clamp(L, 0, o.len);
    while (hi - lo > 1) { var m = (lo + hi) >> 1; if (o.cum[m] <= L) lo = m; else hi = m; }
    var d = o.cum[hi] - o.cum[lo], t = d > 0 ? (L - o.cum[lo]) / d : 0;
    out.x = lerp(o.pts[lo * 2], o.pts[hi * 2], t); out.y = lerp(o.pts[lo * 2 + 1], o.pts[hi * 2 + 1], t); out.i = lo;
    return out;
  }
  function inside(x, y, c) { // ray cast against a contour polyline
    var inn = false, p = c.pts;
    for (var i = 0, j = c.n - 1; i < c.n; j = i++) {
      var xi = p[i * 2], yi = p[i * 2 + 1], xj = p[j * 2], yj = p[j * 2 + 1];
      if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inn = !inn;
    }
    return inn;
  }

  /* ---- SVG helpers: every closed (sub)path -> polyline in root user units (transforms applied) ---- */
  function mountSVG(text) {
    var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    var host = D.createElementNS(NS, 'svg');
    host.setAttribute('style', 'position:absolute;left:-9999px;top:0;width:10px;height:10px;visibility:hidden');
    D.body.appendChild(host);
    var svg = D.importNode(doc.documentElement, true); host.appendChild(svg);
    return { host: host, svg: svg, done: function () { D.body.removeChild(host); } };
  }
  function toRoot(svg, el) { var m = svg.getCTM().inverse().multiply(el.getCTM()); return function (x, y) { var p = svg.createSVGPoint(); p.x = x; p.y = y; p = p.matrixTransform(m); return p; }; }
  function shapeContours(svg, el, step) {
    var out = [], tag = el.tagName.toLowerCase();
    // Split multi-subpath paths: sample each subpath on the cumulative prefix path (moveto adds no length),
    // which keeps relative commands correct.
    var parts = tag === 'path' ? (el.getAttribute('d') || '').split(/(?=[Mm])/).filter(function (s) { return /\d/.test(s); }) : [null];
    var tmp = el;
    if (tag === 'path' && parts.length > 1) { tmp = D.createElementNS(NS, 'path'); el.parentNode.appendChild(tmp); }
    var tr = toRoot(svg, el), prevL = 0;
    for (var i = 0; i < parts.length; i++) {
      if (tmp !== el) tmp.setAttribute('d', parts.slice(0, i + 1).join(' '));
      var L = tmp.getTotalLength(), sl = L - prevL, n = Math.max(24, Math.ceil(sl / step)), a = [];
      for (var k = 1; k <= n; k++) { var g = tmp.getPointAtLength(prevL + sl * k / n), p = tr(g.x, g.y); a.push(p.x, p.y); }
      prevL = L;
      if (sl > 1e-3) out.push(a);
    }
    if (tmp !== el) tmp.parentNode.removeChild(tmp);
    return out;
  }
  /* logo SVG -> raw contours in SVG user units (SLOW: fallback only - normally mac-laser-logo-points.json is used).
     The raw contours don't depend on the layout, so they are traced once and fitted into each part's box. */
  function traceLogo(text) {
    var m = mountSVG(text), raw = [];
    [].slice.call(m.svg.querySelectorAll('path,rect,polygon,ellipse')).forEach(function (el) {
      var bb = el.getBBox(); raw = raw.concat(shapeContours(m.svg, el, Math.max(bb.width, bb.height) / 900));
    });
    m.done();
    return raw;
  }
  /* logo SVG -> cut groups, fitted into box (mm) */
  function logoFromSVG(text, box) { return buildLogo(traceLogo(text), box); }
  /* validate the pre-traced points file: { contours: [[x,y,x,y,...], ...] } */
  function rawFromPoints(data) {
    var c = data && data.contours;
    if (!Array.isArray(c) || !c.length) throw new Error('no contours');
    return c.map(function (a) {
      if (!Array.isArray(a) || a.length < 6 || a.length % 2) throw new Error('bad contour');
      return a.map(Number);
    });
  }
  function buildLogo(raw, box) {
    if (!raw.length) return null;
    var x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9, i;
    raw.forEach(function (a) { for (var k = 0; k < a.length; k += 2) { x0 = Math.min(x0, a[k]); x1 = Math.max(x1, a[k]); y0 = Math.min(y0, a[k + 1]); y1 = Math.max(y1, a[k + 1]); } });
    if (box) { // fit (keep aspect) and centre in the box
      var sc = Math.min(box.w / (x1 - x0), box.h / (y1 - y0)), ox = box.x + (box.w - (x1 - x0) * sc) / 2, oy = box.y + (box.h - (y1 - y0) * sc) / 2;
      raw = raw.map(function (a) { return a.map(function (v, k) { return k % 2 ? oy + (v - y0) * sc : ox + (v - x0) * sc; }); });
    }
    var cs = raw.map(makeContour).filter(function (c) { return c.len > 1; });
    // nesting: even depth = outer edge of a cut-out, odd depth = an island (e.g. the counter of a letter)
    cs.forEach(function (c) {
      c.depth = 0; c.parent = null;
      cs.forEach(function (o) { if (o !== c && o.area > c.area && inside(c.pts[0], c.pts[1], o)) { c.depth++; if (!c.parent || o.area < c.parent.area) c.parent = o; } });
    });
    var groups = cs.filter(function (c) { return c.depth % 2 === 0; }).map(function (o) {
      var holes = cs.filter(function (c) { return c.parent === o && c.depth === o.depth + 1; });
      var path = new Path2D(); path.addPath(o.path); holes.forEach(function (h) { path.addPath(h.path); });
      return { outer: o, contours: holes.concat([o]), path: path, x: o.bbox.x }; // islands first, like real CAM
    });
    groups.sort(function (a, b) { return a.x - b.x; }); // cut left to right, like writing it
    var seq = [], L = 0, TP = 45; // TP = travel + pierce, in "mm of cutting" units
    groups.forEach(function (g, gi) {
      g.contours.forEach(function (c) { seq.push({ c: c, g: gi, s0: L, s1: L + TP, s2: L + TP + c.len }); L += TP + c.len; });
      g.endL = L;
    });
    return { groups: groups, seq: seq, L: L };
  }
  /* part SVG (from the DXF) -> part */
  function partFromSVG(text, name) {
    var m = mountSVG(text), svg = m.svg;
    var holes = [].slice.call(svg.querySelectorAll('circle')).map(function (c) {
      var p = toRoot(svg, c)(+c.getAttribute('cx'), +c.getAttribute('cy')); return { x: p.x, y: p.y, r: +c.getAttribute('r') };
    });
    var lb = svg.querySelector('#logo-box'), logoBox = null;
    if (lb) { var b = lb.getBBox(), t = toRoot(svg, lb), p0 = t(b.x, b.y), p1 = t(b.x + b.width, b.y + b.height); logoBox = { x: Math.min(p0.x, p1.x), y: Math.min(p0.y, p1.y), w: Math.abs(p1.x - p0.x), h: Math.abs(p1.y - p0.y) }; }
    var raw = [];
    [].slice.call(svg.querySelectorAll('path,rect,polygon,ellipse')).forEach(function (el) { if (el !== lb) raw = raw.concat(shapeContours(svg, el, 2)); });
    m.done();
    var cs = raw.map(makeContour), outline = cs.reduce(function (a, c) { return !a || c.area > a.area ? c : a; }, null);
    var ox = outline.bbox.x, oy = outline.bbox.y, shift = function (a) { return a.map(function (v, k) { return v - (k % 2 ? oy : ox); }); };
    holes.forEach(function (h) { h.x -= ox; h.y -= oy; });
    if (logoBox) { logoBox.x -= ox; logoBox.y -= oy; }
    holes.sort(function (a, b) { return a.y - b.y || a.x - b.x; });
    var rows = [], cur = null;
    holes.forEach(function (h) { if (!cur || h.y - cur.y > Math.max(h.r * 1.5, 4)) { cur = { y: h.y, list: [] }; rows.push(cur); } cur.list.push(h); });
    var ordered = [];
    rows.forEach(function (r, ri) { r.list.sort(function (a, b) { return ri % 2 ? a.x - b.x : b.x - a.x; }); ordered = ordered.concat(r.list); });
    var grid = null, xs = uniq(holes.map(function (h) { return h.x; })), ys = uniq(holes.map(function (h) { return h.y; })), i;
    if (xs.length > 1 && ys.length > 1) {
      var pitch = xs[1] - xs[0], regular = true;
      for (i = 1; i < xs.length; i++) if (Math.abs(xs[i] - xs[i - 1] - pitch) > .5) regular = false;
      for (i = 1; i < ys.length; i++) if (Math.abs(ys[i] - ys[i - 1] - pitch) > .5) regular = false;
      if (regular) grid = { x0: xs[0], y0: ys[0], pitch: pitch, cols: xs.length, rows: ys.length };
    }
    var cut = raw.filter(function (a, k) { return cs[k] !== outline; }).map(shift);
    var pt = { name: name || '16D table top', w: outline.bbox.w, h: outline.bbox.h, holes: ordered, outline: makeContour(shift(raw[cs.indexOf(outline)])),
      grid: grid, logoBox: logoBox, clear: logoBox, logo: cut.length ? buildLogo(cut, null) : null };
    return finishPart(pt);
  }
  function uniq(a) {
    a = a.map(function (v) { return Math.round(v * 10) / 10; }).sort(function (x, y) { return x - y; });
    return a.filter(function (v, i) { return !i || v - a[i - 1] > .5; });
  }

  /* =====================================================================
     2. SMALL TOUCHES (forge letters, magnetic buttons, laser-dot cursor)
     ===================================================================== */
  var forge = q('[data-mlh-forge]', root);
  if (forge) {
    var idx = 0;
    forge.innerHTML = forge.textContent.trim().split(' ').map(function (w) {
      return '<span style="white-space:nowrap">' + w.split('').map(function (ch) { return '<span class="mlh-ch" style="--i:' + (idx++) + '">' + ch + '</span>'; }).join('') + '</span>';
    }).join(' ');
    if (!RM) forge.classList.add('mlh-forge');
  }
  if (FINE && !RM && cfg.magnet === 'true') (function () {
    var mags = [].slice.call(root.querySelectorAll('.mlh-magnet')).map(function (el) { return { el: el, in: el.firstElementChild, x: 0, y: 0, tx: 0, ty: 0 }; });
    var raf = 0;
    function tick() {
      raf = 0; var moving = false;
      mags.forEach(function (m) {
        m.x += (m.tx - m.x) * .2; m.y += (m.ty - m.y) * .2;
        if (Math.abs(m.tx - m.x) > .05 || Math.abs(m.ty - m.y) > .05) moving = true; else { m.x = m.tx; m.y = m.ty; }
        m.el.style.transform = 'translate3d(' + m.x.toFixed(2) + 'px,' + m.y.toFixed(2) + 'px,0)';
        if (m.in) m.in.style.transform = 'translate3d(' + (m.x * .35).toFixed(2) + 'px,' + (m.y * .35).toFixed(2) + 'px,0)';
      });
      if (moving) raf = requestAnimationFrame(tick);
    }
    W0.addEventListener('pointermove', function (e) {
      mags.forEach(function (m) {
        var r = m.el.getBoundingClientRect(), cx = r.left - m.x + r.width / 2, cy = r.top - m.y + r.height / 2, dx = e.clientX - cx, dy = e.clientY - cy;
        var inn = Math.abs(dx) < r.width / 2 + 46 && Math.abs(dy) < r.height / 2 + 36;
        m.tx = inn ? clamp(dx * .2, -10, 10) : 0; m.ty = inn ? clamp(dy * .3, -8, 8) : 0;
      });
      if (!raf) raf = requestAnimationFrame(tick);
    }, { passive: true });
  })();
  /* Laser-dot pointer: mouse / fine pointers only, never with reduced motion, never on touch.
     It hides itself (and the normal cursor comes back) over the cart drawer, cart, product forms,
     quick view / modals and form fields. */
  if (FINE && !RM && cfg.pointer === 'true') (function () {
    var xh = D.querySelector('.mlh-xhair'); if (!xh) return;
    if (xh.parentNode !== D.body) D.body.appendChild(xh); // fixed-position layer outside the section
    var dot = q('.mlh-xh-dot', xh), ring = q('.mlh-xh-ring', xh);
    var EXCL = 'm-cart-drawer,.m-cart-drawer,m-cart,.m-cart,#cart-drawer-form,cart-drawer,#CartDrawer,' +
      'product-form,.m-product-form,form[action*="/cart"],.shopify-payment-button,[id*="quick-view"],.m-quick-view,' +
      '.m-modal,m-modal,dialog,[role="dialog"],[aria-modal="true"],input,select,textarea,iframe';
    var mx = -100, my = -100, rx = -100, ry = -100, raf = 0, active = false;
    // is the cart drawer / a quick view / a modal open? (checked at most every 150 ms; Minimog keeps closed modals in the DOM)
    var chkT = -1e9, chkV = false;
    function shown(e) { var r = e.getBoundingClientRect(); if (r.width < 2 || r.height < 2) return false; var c = getComputedStyle(e); return c.visibility !== 'hidden' && +c.opacity > .05; }
    function drawerOpen() {
      var now = performance.now(); if (now - chkT < 150) return chkV; chkT = now;
      chkV = !!D.querySelector('m-cart-drawer.m-cart-drawer--active,.m-cart-drawer--active,dialog[open]') ||
        [].some.call(D.querySelectorAll('[aria-modal="true"],[role="dialog"],.m-modal,m-modal,[id*="quick-view"]'), shown);
      return chkV;
    }
    function setActive(on) {
      if (on === active) return; active = on;
      html.classList.toggle('mlh-xh', on);
      if (!on) xh.classList.remove('mlh-on');
    }
    function tick() {
      raf = 0; rx += (mx - rx) * .3; ry += (my - ry) * .3;
      ring.style.transform = 'translate3d(' + rx.toFixed(1) + 'px,' + ry.toFixed(1) + 'px,0)';
      if (Math.abs(mx - rx) + Math.abs(my - ry) > .2) raf = requestAnimationFrame(tick);
    }
    W0.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') { setActive(false); return; }
      var t = e.target, off = drawerOpen() || !!(t && t.closest && t.closest(EXCL));
      if (off) { setActive(false); return; }
      if (!active) { rx = mx = e.clientX; ry = my = e.clientY; }
      setActive(true);
      mx = e.clientX; my = e.clientY; xh.classList.add('mlh-on');
      dot.style.transform = 'translate3d(' + mx + 'px,' + my + 'px,0)';
      xh.classList.toggle('mlh-hot', !!(t && t.closest && t.closest('a,button,[role="button"],summary,label')));
      if (!raf) raf = requestAnimationFrame(tick);
    }, { passive: true });
    W0.addEventListener('pointerdown', function (e) { if (e.pointerType && e.pointerType !== 'mouse') setActive(false); }, { passive: true });
    // opening the cart drawer by click: hide straight away, the next pointermove re-evaluates
    D.addEventListener('click', function () { setTimeout(function () { chkT = -1e9; if (drawerOpen()) setActive(false); }, 60); }, true);
    D.addEventListener('mouseleave', function () { xh.classList.remove('mlh-on'); });
    D.addEventListener('mouseenter', function () { if (active) xh.classList.add('mlh-on'); });
  })();

  /* =====================================================================
     3. THE LASER STAGE
     ===================================================================== */
  var stage = q('.mlh-stage', root), canvas = stage && q('.mlh-steel', stage), video = stage && q('.mlh-intro', stage), hero = root, stick = q('.mlh-stick', root);
  var ro = { el: q('.mlh-readout', root), txt: q('.mlh-ro-txt', root), bar: q('.mlh-ro-bar i', root) };
  if (RM || !stage || !canvas || !canvas.getContext) return; // reduced motion / no canvas: finished top + 100% readout stay (static HTML)
  var ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) { root.classList.add('mlh--rm'); return; }

  var kind = '', W = 0, H = 0, dpr = 1, part = null, stock = null, tex = null, texBase = null, texCtx = null, texTS = 1, texK = 0, texG = 0, overlay = null, TL = null, cam = {};
  var partDesk = buildGridPart(TOP_16D), partMob = buildGridPart(SECTION_16D);
  var disp = 0, target = 0, raf = 0, last = 0, inView = true, ready = false, steelShown = false, mob = { start: 0, dist: 1 }, stickTop = 0;
  var prevHoleF = 0, prevOutF = 0, prevLogoL = 0, perf = [];
  //   h0-h1 holes | h1-g1 logo (first move included) | g1-t1 travel | t1-l0 pierce | l0-o0 lead-in | o0-o1 outline | d0-d1 lift
  var TLS = {
    desk: { h0: .04, h1: .55, z0: .53, z1: .59, z2: .775, z3: .83, g1: .79, t1: .815, l0: .832, o0: .838, o1: .94, d0: .948, d1: 1 },
    mob: { h0: .03, h1: .56, z0: .545, z1: .6, z2: .755, z3: .8, g1: .77, t1: .79, l0: .805, o0: .81, o1: .925, d0: .93, d1: 1 }
  };
  var CAP = { desk: 260, mob: 70 }, PER_FRAME = { desk: 22, mob: 6 };

  function sprite(stops, size) {
    var c = D.createElement('canvas'); c.width = c.height = size; var g = c.getContext('2d'), r = size / 2;
    var gr = g.createRadialGradient(r, r, 0, r, r, r); stops.forEach(function (s) { gr.addColorStop(s[0], s[1]); });
    g.fillStyle = gr; g.fillRect(0, 0, size, size); return c;
  }
  var SPR = {
    hot: sprite([[0, 'rgba(255,255,255,1)'], [.08, 'rgba(255,244,214,.95)'], [.22, 'rgba(255,176,82,.55)'], [.5, 'rgba(255,96,24,.16)'], [1, 'rgba(255,60,0,0)']], 128),
    ember: sprite([[0, 'rgba(255,150,60,.9)'], [.4, 'rgba(255,80,20,.3)'], [1, 'rgba(255,40,0,0)']], 64),
    pilot: sprite([[0, 'rgba(255,90,90,1)'], [.25, 'rgba(255,30,30,.55)'], [1, 'rgba(255,0,0,0)']], 64),
    shadow: sprite([[0, 'rgba(0,0,0,.7)'], [.55, 'rgba(0,0,0,.35)'], [1, 'rgba(0,0,0,0)']], 64)
  };

  // --- sparks: fixed pool, screen space (CSS px) ---
  var SP = { x: new Float32Array(260), y: new Float32Array(260), vx: new Float32Array(260), vy: new Float32Array(260), l: new Float32Array(260), m: new Float32Array(260), n: 0 };
  var rnd = Math.random;
  function emit(x, y, n, power) {
    var cap = CAP[kind], sc = kind === 'desk' ? 1 : .6;
    for (var i = 0; i < n && SP.n < cap; i++) {
      var j = SP.n++, a = rnd() * TAU, sp = (50 + Math.pow(rnd(), 1.7) * 520 * power) * sc;
      SP.x[j] = x; SP.y[j] = y; SP.vx[j] = Math.cos(a) * sp; SP.vy[j] = Math.sin(a) * sp * .8 - 60 * sc;
      SP.l[j] = SP.m[j] = .18 + rnd() * .5;
    }
  }
  function stepSparks(dt) {
    var drag = Math.pow(.12, dt), g = (kind === 'desk' ? 900 : 600) * dt;
    for (var i = 0; i < SP.n; i++) {
      SP.l[i] -= dt;
      if (SP.l[i] <= 0) { var e = --SP.n; SP.x[i] = SP.x[e]; SP.y[i] = SP.y[e]; SP.vx[i] = SP.vx[e]; SP.vy[i] = SP.vy[e]; SP.l[i] = SP.l[e]; SP.m[i] = SP.m[e]; i--; continue; }
      SP.vx[i] *= drag; SP.vy[i] = SP.vy[i] * drag + g; SP.x[i] += SP.vx[i] * dt; SP.y[i] += SP.vy[i] * dt;
    }
  }
  var SPC = ['rgba(255,248,222,1)', 'rgba(255,184,84,.9)', 'rgba(255,96,30,.75)'], SPW = [2, 1.5, 1.1];
  function drawSparks(c) {
    if (!SP.n) return;
    c.globalCompositeOperation = 'lighter'; c.lineCap = 'round';
    for (var b = 0; b < 3; b++) {
      c.beginPath();
      for (var i = 0; i < SP.n; i++) {
        var t = SP.l[i] / SP.m[i], bb = t > .62 ? 0 : t > .3 ? 1 : 2; if (bb !== b) continue;
        c.moveTo(SP.x[i], SP.y[i]); c.lineTo(SP.x[i] - SP.vx[i] * .03, SP.y[i] - SP.vy[i] * .03);
      }
      c.strokeStyle = SPC[b]; c.lineWidth = SPW[b]; c.stroke();
    }
    c.globalCompositeOperation = 'source-over';
  }

  // --- layout + camera + textures ---
  function fitCam(s) { // camera for a given scale: desktop anchors the top to the right, mobile centres it
    var room = H - cam.padT - cam.padB, ph = part.h * s;
    return { s: s, tx: kind === 'desk' ? W - cam.padR - part.w * s : (W - part.w * s) / 2, ty0: ph <= room ? cam.padT + (room - ph) / 2 : cam.padT, ty1: ph <= room ? cam.padT + (room - ph) / 2 : H - cam.padB - ph };
  }
  function logoRect() { // area the camera moves in on for the logo phase
    if (part.logoBox) return part.logoBox;
    if (!part.logo) return null;
    var x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    part.logo.groups.forEach(function (g) { var b = g.outer.bbox; x0 = Math.min(x0, b.x); y0 = Math.min(y0, b.y); x1 = Math.max(x1, b.x + b.w); y1 = Math.max(y1, b.y + b.h); });
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }
  function logoCam() { // move in on the logo: desktop ~44% of the hero width over the right-hand side, mobile ~74% of the panel
    var r = logoRect(), desk = kind === 'desk'; if (!r) return null;
    var s = Math.min(W * (desk ? .44 : .74) / r.w, (H - cam.padT - cam.padB) * .8 / r.h, cam.s1 * (desk ? 1.6 : 1.75));
    var cx = desk ? W * .635 : W / 2, cy = cam.padT + (H - cam.padT - cam.padB) / 2;
    return { s: s, tx: cx - (r.x + r.w / 2) * s, ty: cy - (r.y + r.h / 2) * s };
  }
  function layout(force) {
    var nk = mqDesk.matches ? 'desk' : 'mob';
    var r = stage.getBoundingClientRect(), nw = Math.round(r.width), nh = Math.round(r.height), vh = W0.innerHeight;
    if (nk === 'mob') { var top = r.top + W0.pageYOffset; mob.start = Math.max(0, top - vh * .62); mob.dist = vh * .5; }
    stickTop = parseFloat(getComputedStyle(stick).top) || 0;
    if (!force && nk === kind && nw === W && nh === H) return false;
    var kindChanged = nk !== kind; kind = nk; W = nw; H = nh;
    TL = TLS[kind];
    part = kind === 'desk' ? partDesk : partMob;
    var maxPx = kind === 'desk' ? 3.0e6 : 1.1e6;
    dpr = Math.min(W0.devicePixelRatio || 1, 2); if (W * H * dpr * dpr > maxPx) dpr = Math.sqrt(maxPx / (W * H));
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    if (kind === 'desk') {
      cam.padT = 70; cam.padB = 108; cam.padR = Math.max(48, W * .035);
      // the top covers at most ~62% of the width, so the cutting happens across the centre and right
      cam.s0 = Math.min((H - cam.padT - cam.padB) / part.h, (W * .62 - cam.padR) / part.w);
      cam.s1 = Math.min(cam.s0 * 1.24, (W * .8 - cam.padR) / part.w);
    } else {
      cam.padT = 38; cam.padB = 62; cam.padR = 20;
      cam.s0 = cam.s1 = Math.min((W - 40) / part.w, (H - cam.padT - cam.padB) / part.h);
    }
    var cs = [fitCam(cam.s0), fitCam(cam.s1)], m = 90, left = 0, right = 0, topN = 0, botN = 0;
    cam.L = logoCam();
    if (cam.L) cs.push({ s: cam.L.s, tx: cam.L.tx, ty0: cam.L.ty, ty1: cam.L.ty });
    cs.forEach(function (a) {
      left = Math.max(left, a.tx / a.s); right = Math.max(right, (W - a.tx - part.w * a.s) / a.s);
      topN = Math.max(topN, a.ty0 / a.s); botN = Math.max(botN, (H - a.ty1 - part.h * a.s) / a.s);
    });
    left += m; right += m; topN += m; botN += m;
    stock = { x: -left, y: -topN, w: part.w + left + right, h: part.h + topN + botN };
    small = null; tex = buildTexture(); overlay = buildOverlay();
    if (kindChanged && ready && !steelShown) showSteel();
    return true;
  }
  function buildTexture() {
    var ts = Math.min(Math.max(cam.s1, cam.L ? cam.L.s : 0) * dpr, 3072 / Math.max(stock.w, stock.h));
    var tw = Math.ceil(stock.w * ts), th = Math.ceil(stock.h * ts);
    var cv = D.createElement('canvas'); cv.width = tw; cv.height = th;
    var g = cv.getContext('2d'), R = rng(1616), i, x, y;
    var gr = g.createLinearGradient(0, 0, tw, th);
    gr.addColorStop(0, '#5a6571'); gr.addColorStop(.4, '#414a54'); gr.addColorStop(1, '#2c3239');
    g.fillStyle = gr; g.fillRect(0, 0, tw, th);
    var blobs = Math.round(80 * stock.w * stock.h / 3e6) + 30;
    for (i = 0; i < blobs; i++) { // pickled/mill-scale mottling
      x = R() * tw; y = R() * th; var rad = (40 + R() * 260) * ts, light = R() < .45;
      var rg = g.createRadialGradient(x, y, 0, x, y, rad);
      rg.addColorStop(0, light ? 'rgba(170,192,222,.09)' : 'rgba(8,11,16,.2)'); rg.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = rg; g.fillRect(x - rad, y - rad, rad * 2, rad * 2);
    }
    for (y = 0; y < th; y++) { // rolling streaks
      if (R() < .5) { g.fillStyle = R() < .5 ? 'rgba(205,220,240,' + (R() * .04).toFixed(3) + ')' : 'rgba(0,0,0,' + (R() * .07).toFixed(3) + ')'; g.fillRect(R() * tw * .6, y, tw * (.2 + R() * .8), R() < .1 ? 2 : 1); }
    }
    var nc = D.createElement('canvas'); nc.width = nc.height = 128; var nx = nc.getContext('2d'), id = nx.createImageData(128, 128);
    for (i = 0; i < 128 * 128; i++) { var v = R() * 255 | 0; id.data[i * 4] = id.data[i * 4 + 1] = id.data[i * 4 + 2] = v; id.data[i * 4 + 3] = 20; }
    nx.putImageData(id, 0, 0); g.fillStyle = g.createPattern(nc, 'repeat'); g.fillRect(0, 0, tw, th);
    // world-space etching (mm)
    g.setTransform(ts, 0, 0, ts, -stock.x * ts, -stock.y * ts);
    var G = part.grid;
    if (G) {
      var p = new Path2D(), xa = G.x0 - G.pitch / 2, ya = G.y0 - G.pitch / 2, xb = G.x0 + (G.cols - .5) * G.pitch, yb = G.y0 + (G.rows - .5) * G.pitch;
      for (i = 0; i <= G.cols; i++) { p.moveTo(xa + i * G.pitch, ya); p.lineTo(xa + i * G.pitch, yb); }
      for (i = 0; i <= G.rows; i++) { p.moveTo(xa, ya + i * G.pitch); p.lineTo(xb, ya + i * G.pitch); }
      g.save();
      if (part.clear) { var cp = new Path2D(); cp.rect(stock.x, stock.y, stock.w, stock.h); var C = part.clear; cp.rect(C.x + .6, C.y + .6, C.w - 1.2, C.h - 1.2); g.clip(cp, 'evenodd'); }
      g.lineWidth = 1.2 / ts; g.strokeStyle = 'rgba(0,0,0,.34)'; g.save(); g.translate(.7 / ts, .7 / ts); g.stroke(p); g.restore();
      g.strokeStyle = 'rgba(210,224,242,.12)'; g.stroke(p);
      g.restore();
      g.fillStyle = 'rgba(220,230,244,.2)'; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.font = '800 7px ' + HFONT;
      g.fillText('16D', G.x0 + G.pitch / 2, G.y0 + (G.rows - 1.5) * G.pitch + .5);
    }
    texBase = cv; texTS = ts;
    var wc = D.createElement('canvas'); wc.width = tw; wc.height = th; texCtx = wc.getContext('2d'); texCtx.drawImage(cv, 0, 0); texK = texG = 0;
    return wc;
  }
  /* Holes and finished logo pieces are baked into the working texture as they are cut (cheap per frame).
     Scrolling back up restores the pristine sheet and re-bakes up to the current point. */
  function bake(k, gd) {
    if (k === texK && gd === texG) return;
    var g = texCtx, i;
    g.setTransform(1, 0, 0, 1, 0, 0);
    if (k < texK || gd < texG) { g.drawImage(texBase, 0, 0); texK = texG = 0; small = null; }
    g.setTransform(texTS, 0, 0, texTS, -stock.x * texTS, -stock.y * texTS);
    var lw = 1.1 / cam.s1;
    if (k > texK) {
      var f = new Path2D(), r = new Path2D(), holes = part.holes;
      for (i = texK; i < k; i++) {
        var h = holes[i];
        f.moveTo(h.x + h.r, h.y); f.arc(h.x, h.y, h.r, 0, TAU);
        r.moveTo(h.x + h.r * Math.cos(.12 * PI), h.y + h.r * Math.sin(.12 * PI)); r.arc(h.x, h.y, h.r, .12 * PI, .88 * PI);
      }
      g.fillStyle = '#030405'; g.fill(f);
      g.lineWidth = lw; g.strokeStyle = 'rgba(190,206,228,.3)'; g.stroke(r);
      texK = k;
    }
    if (gd > texG && part.logo) {
      for (i = texG; i < gd; i++) {
        var gp = part.logo.groups[i];
        g.fillStyle = '#030405'; g.fill(gp.path, 'evenodd');
        g.save(); g.translate(.5 / cam.s1, .8 / cam.s1); g.lineWidth = lw; g.strokeStyle = 'rgba(190,206,228,.28)'; g.stroke(gp.path); g.restore();
      }
      texG = gd;
    }
  }
  var small = null; // copy at the zoomed-out scale once everything inside the top is cut
  function nGroups() { return part.logo ? part.logo.groups.length : 0; }
  function smallTex() {
    if (small) return small;
    bake(part.holes.length, nGroups());
    var ts = cam.s0 * dpr, tw = Math.ceil(stock.w * ts), th = Math.ceil(stock.h * ts), mk = function (src) {
      var c = D.createElement('canvas'); c.width = tw; c.height = th; var g = c.getContext('2d'); g.imageSmoothingQuality = 'high'; g.drawImage(src, 0, 0, tw, th); return c;
    };
    small = { ts: ts, cut: mk(tex), base: mk(texBase) }; return small;
  }
  function blitTex(c, s, tx, ty) {
    var img = tex, ts = texTS;
    if (texK === part.holes.length && texG === nGroups() && s <= cam.s0 * 1.12 && cam.s0 < cam.s1) { var sm = smallTex(); img = sm.cut; ts = sm.ts; }
    if (Math.abs(s * dpr - ts) < 1e-4) { // 1:1 device-pixel blit (fast path)
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.drawImage(img, Math.round((tx + stock.x * s) * dpr), Math.round((ty + stock.y * s) * dpr));
      c.setTransform(dpr * s, 0, 0, dpr * s, dpr * tx, dpr * ty);
    } else c.drawImage(img, stock.x, stock.y, stock.w, stock.h);
  }
  function buildOverlay() {
    var c = D.createElement('canvas'); c.width = canvas.width; c.height = canvas.height;
    var g = c.getContext('2d'); g.scale(dpr, dpr);
    var lg = g.createLinearGradient(0, 0, W, H);
    lg.addColorStop(0, 'rgba(140,178,235,.13)'); lg.addColorStop(.32, 'rgba(140,178,235,.03)'); lg.addColorStop(.6, 'rgba(0,0,0,0)'); lg.addColorStop(1, 'rgba(0,0,0,.3)');
    g.fillStyle = lg; g.fillRect(0, 0, W, H);
    g.save(); g.translate(W * .6, H * .5); g.rotate(-.5);
    [[-.14, .06, .09], [.12, .03, .06]].forEach(function (b) {
      var y = b[0] * Math.max(W, H), hw = b[1] * Math.max(W, H), bg = g.createLinearGradient(0, y - hw, 0, y + hw);
      bg.addColorStop(0, 'rgba(170,200,255,0)'); bg.addColorStop(.5, 'rgba(170,200,255,' + b[2] + ')'); bg.addColorStop(1, 'rgba(170,200,255,0)');
      g.fillStyle = bg; g.fillRect(-W * 1.5, y - hw, W * 3, hw * 2);
    });
    g.restore();
    var cx = kind === 'desk' ? W * .66 : W * .5, rg = g.createRadialGradient(cx, H * .47, Math.min(W, H) * .3, cx, H * .47, Math.max(W, H) * .8);
    rg.addColorStop(0, 'rgba(0,0,0,0)'); rg.addColorStop(1, 'rgba(0,0,0,.6)');
    g.fillStyle = rg; g.fillRect(0, 0, W, H);
    return c;
  }

  // --- timeline -> state ---
  var S = {}, tmp = { x: 0, y: 0 }, tmp2 = { x: 0, y: 0 };
  function leadPt(h, out) { out.x = h.x; out.y = h.y - h.r; return out; }
  function camFor(p, st) {
    // holes: pan down the top at s1 -> logo: move in on the logo -> outline: pull back to show the whole top
    var a = fitCam(cam.s1), b = fitCam(cam.s0), t = clamp(st.holeF / part.holes.length, 0, 1);
    var A = { s: a.s, tx: a.tx, ty: lerp(a.ty0, a.ty1, t) }, B = { s: b.s, tx: b.tx, ty: b.ty0 };
    var L = (cam.L && part.logo) ? cam.L : { s: a.s, tx: a.tx, ty: a.ty1 };
    var zi = easeIO(seg(p, TL.z0, TL.z1)), zo = easeIO(seg(p, TL.z2, TL.z3));
    var c1 = camMix(A, L, zi), c2 = camMix(c1, B, zo);
    st.s = c2.s; st.tx = c2.tx; st.ty = c2.ty;
  }
  function camMix(a, b, k) { // zoom geometrically about a linearly moving view centre
    if (k <= 0) return a; if (k >= 1) return b;
    var ax = (W / 2 - a.tx) / a.s, ay = (H / 2 - a.ty) / a.s, bx = (W / 2 - b.tx) / b.s, by = (H / 2 - b.ty) / b.s;
    var s = a.s * Math.pow(b.s / a.s, k), x = lerp(ax, bx, k), y = lerp(ay, by, k);
    return { s: s, tx: W / 2 - x * s, ty: H / 2 - y * s };
  }
  function evalState(p) {
    var st = S, holes = part.holes, N = holes.length, o = part.outline, lg = part.logo, e;
    var g1 = lg ? TL.g1 : TL.h1;
    st.p = p; st.N = N;
    st.holeF = seg(p, TL.h0, TL.h1) * N; st.k = Math.min(N, Math.floor(st.holeF));
    st.clock = st.holeF + Math.max(0, p - TL.h1) * N / (TL.h1 - TL.h0); // keeps running so the last holes cool down
    st.logoL = lg ? seg(p, TL.h1, g1) * lg.L : 0;
    st.lclock = lg ? st.logoL + Math.max(0, p - g1) * lg.L / (g1 - TL.h1) : 0;
    st.gd = 0; st.ci = -1; st.cutL = 0;
    if (lg) { var gs = lg.groups; while (st.gd < gs.length && gs[st.gd].endL <= st.logoL + 1e-6) st.gd++; }
    st.cur = -1; st.cutA = 0; st.pierce = 0; st.outF = 0; st.drop = 0; st.freed = false; st.on = false; st.head = true;
    var park = { x: part.w + 60, y: -45 };
    if (p <= TL.h0) { st.mode = 'park'; st.hx = park.x; st.hy = park.y; }
    else if (st.holeF < N) {
      var i = st.k, u = st.holeF - i, h = holes[i], from = i ? leadPt(holes[i - 1], tmp) : park;
      st.cur = i;
      if (u < .22) { e = easeIO(u / .22); st.mode = 'travel'; st.hx = lerp(from.x, h.x, e); st.hy = lerp(from.y, h.y, e); }
      else if (u < .36) { st.mode = 'pierce'; st.on = true; st.hx = h.x; st.hy = h.y; st.pierce = Math.sin(PI * (u - .22) / .14); }
      else if (u < .44) { st.mode = 'lead'; st.on = true; st.hx = h.x; st.hy = h.y - h.r * (u - .36) / .08; }
      else { st.mode = 'cut'; st.on = true; st.cutA = (u - .44) / .56; var th = -PI / 2 + st.cutA * TAU; st.hx = h.x + h.r * Math.cos(th); st.hy = h.y + h.r * Math.sin(th); }
    } else if (lg && p < g1) {
      var sq = lg.seq, ci = 0; while (ci < sq.length - 1 && sq[ci].s2 <= st.logoL) ci++;
      var sc = sq[ci], prev = ci ? sq[ci - 1].c.start : leadPt(holes[N - 1], tmp);
      st.ci = ci;
      if (st.logoL < sc.s1) {
        var v = (st.logoL - sc.s0) / (sc.s1 - sc.s0);
        if (v < .6) { e = easeIO(v / .6); st.mode = 'travel'; st.hx = lerp(prev.x, sc.c.start.x, e); st.hy = lerp(prev.y, sc.c.start.y, e); }
        else { st.mode = 'pierce'; st.on = true; st.hx = sc.c.start.x; st.hy = sc.c.start.y; st.pierce = Math.sin(PI * (v - .6) / .4); }
      } else { st.mode = 'logo'; st.on = true; st.cutL = st.logoL - sc.s1; contourAt(sc.c, st.cutL, tmp2); st.hx = tmp2.x; st.hy = tmp2.y; }
    } else {
      var lp = lg ? lg.seq[lg.seq.length - 1].c.start : leadPt(holes[N - 1], tmp);
      if (p < TL.t1) { e = easeIO(seg(p, g1, TL.t1)); st.mode = 'travel'; st.hx = lerp(lp.x, o.pierce.x, e); st.hy = lerp(lp.y, o.pierce.y, e); }
      else if (p < TL.l0) { st.mode = 'pierce'; st.on = true; st.hx = o.pierce.x; st.hy = o.pierce.y; st.pierce = Math.sin(PI * seg(p, TL.t1, TL.l0)); }
      else if (p < TL.o0) { e = seg(p, TL.l0, TL.o0); st.mode = 'lead'; st.on = true; st.hx = lerp(o.pierce.x, o.start.x, e); st.hy = lerp(o.pierce.y, o.start.y, e); }
      else if (p < TL.o1) { st.mode = 'outline'; st.on = true; st.outF = seg(p, TL.o0, TL.o1); contourAt(o, st.outF * o.len, tmp2); st.hx = tmp2.x; st.hy = tmp2.y; }
      else { st.mode = 'done'; st.outF = 1; st.freed = true; st.head = false; st.drop = seg(p, TL.d0, TL.d1); }
    }
    st.phase = p <= TL.h0 ? 'idle' : st.mode === 'done' ? 'done' : st.holeF < N ? 'holes' : (lg && p < g1) ? 'logo' : 'outline';
    var Wl = lg ? N * .18 : 0, Wo = N * .1;
    st.pct = st.mode === 'done' ? 1 : Math.min(.999, (Math.min(st.holeF, N) + (lg ? st.logoL / lg.L : 0) * Wl + st.outF * Wo) / (N + Wl + Wo));
    camFor(p, st);
    return st;
  }

  // --- drawing ---
  function strokeContour(c, o, L0, L1) {
    var a = contourAt(o, L0, { x: 0, y: 0 }), b = contourAt(o, L1, { x: 0, y: 0 });
    c.beginPath(); c.moveTo(a.x, a.y);
    for (var i = a.i + 1; i <= b.i; i++) c.lineTo(o.pts[i * 2], o.pts[i * 2 + 1]);
    c.lineTo(b.x, b.y);
  }
  function hotTrail(c, o, Lh, s, fade, seglen) {
    for (var i = 0; i < 6; i++) {
      var La = Math.max(0, Lh - (i + 1) * seglen), Lb = Math.max(0, Lh - i * seglen); if (Lb <= La) break;
      strokeContour(c, o, La, Lb); c.strokeStyle = 'rgba(255,' + (200 - i * 25) + ',' + (120 - i * 18) + ',' + ((1 - i / 6) * .85 * fade).toFixed(2) + ')';
      c.lineWidth = (2.2 - i * .2) / s; c.stroke();
    }
  }
  function draw(st) {
    var t0 = performance.now(), c = ctx, s = st.s, tx = st.tx, ty = st.ty, o = part.outline, lg = part.logo, N = part.holes.length, i;
    c.setTransform(dpr, 0, 0, dpr, 0, 0); c.globalAlpha = 1; c.globalCompositeOperation = 'source-over';
    c.fillStyle = '#07080a'; c.fillRect(0, 0, W, H);
    c.setTransform(dpr * s, 0, 0, dpr * s, dpr * tx, dpr * ty);
    var vx0 = -tx / s, vx1 = (W - tx) / s, vy0 = -ty / s, vy1 = (H - ty) / s;
    c.fillStyle = '#101216';
    for (var sx = Math.floor(vx0 / 110) * 110; sx < vx1; sx += 110) c.fillRect(sx, vy0, 5, vy1 - vy0);
    if (!st.freed) {
      bake(st.k, st.gd);
      blitTex(c, s, tx, ty);
      // logo: kerf of the piece being cut (finished pieces are baked in)
      if (st.ci >= 0) {
        var sq = lg.seq, cur = sq[st.ci];
        c.strokeStyle = 'rgba(0,0,0,.9)'; c.lineWidth = Math.max(1.4 / s, 1);
        for (i = st.ci - 1; i >= 0 && sq[i].g === cur.g; i--) { c.stroke(sq[i].c.path); }
        if (st.cutL > 0) { strokeContour(c, cur.c, 0, st.cutL); c.stroke(); }
      }
      if (st.outF > 0) { strokeContour(c, o, 0, st.outF * o.len); c.strokeStyle = 'rgba(0,0,0,.92)'; c.lineWidth = Math.max(1.6 / s, 1.2); c.stroke(); }
    } else {
      var e = easeOut(st.drop), sc = 1 + .035 * e, cx = part.w / 2, cy = part.h / 2, useSmall = cam.s0 < cam.s1;
      c.save(); var sk = new Path2D(); sk.rect(stock.x, stock.y, stock.w, stock.h); sk.addPath(o.path);
      c.clip(sk, 'evenodd'); c.globalAlpha = 1 - e * .88; c.drawImage(useSmall ? smallTex().base : texBase, stock.x, stock.y, stock.w, stock.h); c.restore();
      var rr = Math.max(part.w, part.h) * .85, lg2 = c.createRadialGradient(cx, cy, 0, cx, cy, rr);
      lg2.addColorStop(0, 'rgba(120,140,170,' + (.16 * e).toFixed(3) + ')'); lg2.addColorStop(1, 'rgba(120,140,170,0)');
      c.fillStyle = lg2; c.fillRect(cx - rr, cy - rr, rr * 2, rr * 2);
      c.save(); c.translate(cx, cy); c.scale(sc, sc); c.translate(-cx, -cy);
      c.shadowColor = 'rgba(0,0,0,' + (.85 * e).toFixed(3) + ')'; c.shadowBlur = 56 * e * dpr; c.shadowOffsetX = 10 * e * dpr; c.shadowOffsetY = 30 * e * dpr;
      c.fillStyle = '#16191d'; c.fill(o.path);
      c.shadowColor = 'transparent'; c.shadowBlur = 0; c.shadowOffsetX = c.shadowOffsetY = 0;
      bake(N, nGroups()); c.save(); c.clip(o.path); c.drawImage(useSmall ? smallTex().cut : tex, stock.x, stock.y, stock.w, stock.h); c.restore();
      c.lineWidth = 1.2 / s; c.strokeStyle = 'rgba(215,228,245,' + (.12 + .22 * e).toFixed(3) + ')'; c.stroke(o.path);
      c.restore();
    }
    // heat: recently cut holes and logo pieces glow, then cool
    c.globalCompositeOperation = 'lighter';
    if (!st.freed) {
      for (i = st.k - 1; i >= 0 && i >= st.k - 7; i--) {
        var hh = part.holes[i], heat = 1 - (st.clock - (i + 1)) / 6; if (heat <= 0) continue;
        c.globalAlpha = heat * .5; c.drawImage(SPR.ember, hh.x - hh.r * 2.4, hh.y - hh.r * 2.4, hh.r * 4.8, hh.r * 4.8);
        c.globalAlpha = 1; c.strokeStyle = heat > .7 ? 'rgba(255,224,170,' + heat.toFixed(2) + ')' : 'rgba(255,' + (80 + 140 * heat | 0) + ',30,' + heat.toFixed(2) + ')';
        c.lineWidth = (1 + 1.4 * heat) / s; c.beginPath(); c.arc(hh.x, hh.y, hh.r, 0, TAU); c.stroke();
      }
      if (lg) for (i = st.gd - 1; i >= 0; i--) {
        var gp = lg.groups[i], gh = 1 - (st.lclock - gp.endL) / (lg.L * .3); if (gh <= 0) break;
        c.fillStyle = 'rgba(255,' + (50 + 175 * gh * gh | 0) + ',' + (10 + 110 * gh * gh * gh | 0) + ',' + (.9 * gh).toFixed(3) + ')'; c.fill(gp.path, 'evenodd');
        c.strokeStyle = gh > .7 ? 'rgba(255,226,170,' + gh.toFixed(2) + ')' : 'rgba(255,' + (90 + 130 * gh | 0) + ',30,' + gh.toFixed(2) + ')';
        c.lineWidth = (1 + 1.6 * gh) / s; c.stroke(gp.path);
      }
    }
    if (st.mode === 'cut') {
      var h = part.holes[st.cur], a1 = -PI / 2 + st.cutA * TAU;
      c.strokeStyle = 'rgba(255,110,30,.4)'; c.lineWidth = 4 / s; c.beginPath(); c.arc(h.x, h.y, h.r, -PI / 2, a1); c.stroke();
      c.strokeStyle = 'rgba(255,238,205,.95)'; c.lineWidth = 1.5 / s; c.beginPath(); c.arc(h.x, h.y, h.r, Math.max(-PI / 2, a1 - 1.6), a1); c.stroke();
    }
    if (st.mode === 'logo') hotTrail(c, lg.seq[st.ci].c, st.cutL, s, 1, 9);
    if (st.mode === 'outline' || (st.mode === 'done' && st.drop < .3)) hotTrail(c, o, st.outF * o.len, s, st.mode === 'done' ? 1 - st.drop / .3 : 1, 22);
    c.globalAlpha = 1; c.globalCompositeOperation = 'source-over';
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (st.head && steelShown) { // cutting head: nozzle shadow + beam glow, or a red pilot dot when the beam is off
      var hx = tx + st.hx * s, hy = ty + st.hy * s, big = kind === 'desk' ? 1 : .62;
      c.globalAlpha = .5; c.drawImage(SPR.shadow, hx + 12 * big - 36 * big, hy + 16 * big - 36 * big, 72 * big, 72 * big); c.globalAlpha = 1;
      c.globalCompositeOperation = 'lighter';
      if (st.on) {
        var L2 = 260 * big * (.92 + rnd() * .12); c.globalAlpha = .16 + st.pierce * .12; c.drawImage(SPR.ember, hx - L2 / 2, hy - L2 / 2, L2, L2); c.globalAlpha = 1;
        var G = (100 + st.pierce * 90) * big * (.88 + rnd() * .24);
        c.drawImage(SPR.hot, hx - G / 2, hy - G / 2, G, G);
        c.fillStyle = '#fff'; c.beginPath(); c.arc(hx, hy, 2.2 * big + st.pierce * 1.5, 0, TAU); c.fill();
      } else {
        c.drawImage(SPR.pilot, hx - 10, hy - 10, 20, 20);
        c.fillStyle = '#ff4a4a'; c.beginPath(); c.arc(hx, hy, 1.6, 0, TAU); c.fill();
      }
      c.globalCompositeOperation = 'source-over';
    }
    drawSparks(c);
    c.drawImage(overlay, 0, 0, W, H);
    perf.push(performance.now() - t0); if (perf.length > 600) perf.shift();
  }

  // --- readout ---
  var SEP = '<span class="mlh-ro-sep"> · </span>', roMode = '', roNum = -1, roPct = -1, roBar = -1, roN = null, roP = null;
  var PCT = '<span class="mlh-ro-pct"><span class="mlh-ro-num mlh-ro-p" style="min-width:2ch">0</span>%</span>';
  function readout(st) {
    var mode = st.phase;
    if (mode !== roMode) {
      roMode = mode; roNum = roPct = -1;
      ro.el.classList.toggle('mlh-is-idle', mode === 'idle'); ro.el.classList.toggle('mlh-is-done', mode === 'done');
      ro.txt.innerHTML =
        mode === 'idle' ? '<b class="mlh-ro-state">Ready to cut</b>' + SEP + '16D table top' + SEP + st.N + ' holes + logo' + SEP + PCT :
        mode === 'holes' ? '<b class="mlh-ro-state">Now cutting</b>' + SEP + '16D table top' + SEP + 'hole <span class="mlh-ro-num mlh-ro-n" style="min-width:3ch">1</span> / ' + st.N + SEP + PCT :
        mode === 'logo' ? '<b class="mlh-ro-state">Now cutting</b>' + SEP + 'MAC logo' + SEP + PCT :
        mode === 'outline' ? '<b class="mlh-ro-state">Now cutting</b>' + SEP + 'outline profile' + SEP + PCT :
        '<b class="mlh-ro-state">Cut in Shobdon \u2713</b>' + SEP + '16D top + MAC logo' + SEP + PCT;
      roN = q('.mlh-ro-n', ro.txt); roP = q('.mlh-ro-p', ro.txt);
      stage.classList.toggle('mlh-at-start', mode === 'idle');
    }
    if (roN) { var n = Math.min(st.N, st.k + 1); if (n !== roNum) { roNum = n; roN.textContent = n; } }
    var pc = Math.floor(st.pct * 100); if (pc !== roPct) { roPct = pc; roP.textContent = pc; }
    var b = Math.round(st.pct * 1000); if (b !== roBar) { roBar = b; ro.bar.style.transform = 'scaleX(' + (b / 1000) + ')'; }
  }

  // --- loop ---
  function readTarget() {
    if (kind === 'desk') {
      var r = hero.getBoundingClientRect(), dist = hero.offsetHeight - stick.offsetHeight;
      return dist > 0 ? clamp((stickTop - r.top) / dist, 0, 1) : 0;
    }
    return clamp((W0.pageYOffset - mob.start) / mob.dist, 0, 1);
  }
  function frame(now) {
    raf = 0;
    var dt = clamp((now - last) / 1000, 0, .05); last = now;
    target = readTarget();
    if (!steelShown && target > .012) showSteel();
    var k = 1 - Math.exp(-dt * (kind === 'desk' ? 8 : 10));
    disp += (target - disp) * k; if (Math.abs(target - disp) < 2e-5) disp = target;
    var st = evalState(disp);
    var dH = st.holeF - prevHoleF, dO = (st.outF - prevOutF) * part.outline.len / 50, dL = (st.logoL - prevLogoL) / 50;
    if (st.on && steelShown && st.head && (dH > 1e-5 || dO > 1e-5 || dL > 1e-5)) {
      var hx = st.tx + st.hx * st.s, hy = st.ty + st.hy * st.s;
      emit(hx, hy, Math.min(PER_FRAME[kind], Math.round((st.mode === 'pierce' ? 6 : 2.5) + (dH + dO + dL) * 7)), st.mode === 'pierce' ? 1.25 : 1);
    }
    prevHoleF = st.holeF; prevOutF = st.outF; prevLogoL = st.logoL;
    stepSparks(dt);
    draw(st);
    readout(st);
    if (disp !== target || SP.n > 0) raf = requestAnimationFrame(frame);
  }
  function kick() { if (!raf && ready && inView) { last = performance.now(); raf = requestAnimationFrame(frame); } }

  // --- intro clip -> steel crossfade ---
  function showSteel() {
    if (steelShown) return; steelShown = true;
    stage.classList.add('mlh-is-steel');
    setTimeout(function () { try { video.pause(); } catch (e) {} }, 2300);
    kick();
  }
  function startVideo() {
    var desk = kind === 'desk', playing = false, A = function (n) { return video.getAttribute(n) || ''; };
    if (cfg.intro !== 'true' || /[?&]mlh_skipintro=1/.test(location.search)) { showSteel(); return; }
    // the poster is normally already set by the inline script in the section (so it paints immediately); only set it if missing/different
    var pst = A(desk ? 'data-poster-desk' : 'data-poster-mob');
    if (pst && video.getAttribute('poster') !== pst) video.poster = pst;
    var src = A(desk ? 'data-src-desk' : 'data-src-mob');
    // full-width desktop hero on big / retina screens gets the 1080p clip (2.8 MB)
    if (desk && W * (W0.devicePixelRatio || 1) >= 1700 && A('data-src-hd')) src = A('data-src-hd');
    // H.264 MP4 everywhere it plays (Safari/iOS/Chrome/Edge); VP9 WebM only for browsers without H.264
    if (!video.canPlayType('video/mp4; codecs="avc1.4d401f"') && video.canPlayType('video/webm; codecs="vp9"')) src = A(desk ? 'data-webm-desk' : 'data-webm-mob') || src;
    if (!src) { showSteel(); return; }
    video.src = src;
    video.preload = 'auto'; video.muted = true;
    video.addEventListener('playing', function () { playing = true; });
    video.addEventListener('timeupdate', function () { if (video.duration && video.currentTime > video.duration - 1.1) showSteel(); });
    video.addEventListener('ended', showSteel);
    video.addEventListener('error', function () { setTimeout(showSteel, 800); });
    var go = function () { var pr = video.play(); if (pr && pr.catch) pr.catch(function () { setTimeout(showSteel, 2200); }); };
    if (desk || !('IntersectionObserver' in W0)) go();
    else { var io = new IntersectionObserver(function (es) { if (es[0].isIntersecting) { io.disconnect(); go(); } }, { threshold: .2 }); io.observe(stage); }
    setTimeout(function () { if (!playing) showSteel(); }, desk ? 6000 : 9000);
  }

  function headerHeight() { // the pinned hero sits under the header group (header + announcement bar)
    var h = 0; [].forEach.call(D.querySelectorAll('.shopify-section-group-header-group'), function (s) { h += s.offsetHeight; });
    if (h > 0 && h < W0.innerHeight * .4) root.style.setProperty('--mlh-hh', Math.round(h) + 'px');
  }
  function init() {
    headerHeight();
    layout(true);
    ready = true;
    disp = target = readTarget();
    var st = evalState(disp); draw(st); readout(st); prevHoleF = st.holeF; prevOutF = st.outF; prevLogoL = st.logoL;
    startVideo();
    if (disp > .012) showSteel();
    W0.addEventListener('scroll', kick, { passive: true });
    var rt = 0;
    W0.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(function () { if (W0.pageYOffset < 5) headerHeight(); layout(false); var s2 = evalState(disp); draw(s2); kick(); }, 120); });
    if ('IntersectionObserver' in W0) new IntersectionObserver(function (es) { inView = es[0].isIntersecting; if (inView) kick(); }, { rootMargin: '120px' }).observe(stage);
  }
  function whenFonts() {
    var t = new Promise(function (r) { setTimeout(r, 1200); });
    var f = D.fonts && D.fonts.load ? D.fonts.load('800 40px ' + HFONT).catch(function () {}) : Promise.resolve();
    return Promise.race([f, t]);
  }
  function getText(u) { return fetch(u).then(function (r) { if (!r.ok) throw new Error(r.status + ' ' + u); return r.text(); }); }
  // resolves after the window load event, then waits for an idle slot (used only by the slow tracing fallback)
  function afterLoad() {
    return new Promise(function (res) {
      var idle = function () { if (W0.requestIdleCallback) W0.requestIdleCallback(function () { res(); }, { timeout: 4000 }); else setTimeout(res, 300); };
      if (D.readyState === 'complete') idle(); else W0.addEventListener('load', idle, { once: true });
    });
  }
  // start fetching the pre-traced logo straight away (in parallel with the font wait); resolves to raw contours or null
  var logoPointsP = (LOGO_POINTS_URL ? fetch(LOGO_POINTS_URL).then(function (r) { if (!r.ok) throw new Error(r.status + ' ' + LOGO_POINTS_URL); return r.json(); }).then(rawFromPoints) : Promise.reject(new Error('no points URL')))
    .catch(function (e) { if (LOGO_POINTS_URL) console.warn('Logo points not loaded, will trace the logo SVG after page load', e); return null; });
  var partURL = PART_SVG_URL; // (the preview's ?part= debug flag is deliberately not supported on the live store)
  whenFonts().then(function () {
    if (!partURL) return;
    return getText(partURL).then(function (t) { partDesk = partMob = partFromSVG(t); })
      .catch(function (e) { console.warn('Part SVG not loaded, using the generated 16D grid', e); });
  }).then(function () {
    var need = [partDesk, partMob].filter(function (p, i, a) { return !p.logo && p.logoBox && a.indexOf(p) === i; });
    if (!need.length || (!LOGO_SVG_URL && !LOGO_POINTS_URL)) return;
    var apply = function (raw) { need.forEach(function (p) { p.logo = buildLogo(raw, p.logoBox); }); };
    return logoPointsP.then(function (raw) {
      if (raw) { apply(raw); return; }
      if (!LOGO_SVG_URL) return;
      // fallback: trace the SVG at runtime - once for both layouts, and only after the page has loaded
      return afterLoad().then(function () { return getText(LOGO_SVG_URL); }).then(function (t) { apply(traceLogo(t)); });
    }).catch(function (e) { console.warn('Logo not loaded, cutting the top without it', e); });
  }).then(init);

  // capture hooks (used by the build + Playwright tests)
  W0.__macLaser = {
    info: function () { var st = evalState(disp); return { kind: kind, p: disp, target: target, holeF: st.holeF, N: st.N, mode: st.mode, phase: st.phase, logoGroups: part.logo ? part.logo.groups.length : 0, logoDone: st.gd, pct: st.pct, steel: steelShown, sparks: SP.n, dpr: dpr, W: W, H: H }; },
    perf: function () { return perf.slice(); },
    still: function (type) { steelShown = true; SP.n = 0; disp = target = 1; var st = evalState(1); draw(st); readout(st); return canvas.toDataURL(type || 'image/webp', .86); },
    timeline: function () { return TL; }
  };
})();
